<template>
  <div>
    <v-breadcrumbs
      class="pt-3 pl-7"
      :items="[{ text: 'Presets', exact: true,  to: { name: 'presets' } }, { text: oldPreset.description }]"
    >
    </v-breadcrumbs>
    <v-row
      class="mt-0"
    >

      <v-col
        cols="12"
        md="5"
      >

        <v-card
          flat
          class="mx-3 pb-9"
        >
          <v-toolbar
            dark
            class="tertiary"
            dense
            elevation="0"
          >
            <v-toolbar-title>Preset</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="$refs.duplicatePresetConfirmDialog.show()"
              :disabled="!user.can('preset.create')"
            >
              <v-icon color="secondary" small>fas fa-copy</v-icon>
            </v-btn>
            <v-btn
              icon
              @click="editPresetClick()"
              :disabled="!user.can('preset.update')"
            >
              <v-icon color="secondary" small>fas fa-edit</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text>

            <dl class="row table mt-5">
              <dt class="col-5">Description:</dt>
              <dd class="col-7">{{ oldPreset.description }}</dd>
              <dt class="col-5">Slug:</dt>
              <dd class="col-7">{{ oldPreset.slug }}</dd>
            </dl>

          </v-card-text>

          <preset-dialog
            v-model="presetDialog"
            :model="preset"
            :errors="presetErrors"
            :error="presetError"
            @save="savePresetClick()"
            @delete="(item) => deletePresetItem(item)"
          ></preset-dialog>

          <ConfirmDialog
            ref="duplicatePresetConfirmDialog"
            @confirm="duplicatePreset()"
            title="Confirm"
            message="Are you sure you want to duplicate this preset?"
            labelConfirm="Duplicate"
            labelCancel="Cancel"
          ></ConfirmDialog>

        </v-card>


      </v-col>


      <v-col
        cols="12"
        md="7"
      >
        <v-card
          flat
          class="mx-3 pb-9"
        >
          <v-toolbar
            dark
            class="tertiary"
            dense
            elevation="0"
          >
            <v-toolbar-title>Surface Decorations</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-text-field
              v-if="false"
              dense
              background-color="primary"
              v-model="surfaceDecorationFilter.query"
              label="Search..."
              single-line
              hide-details
              solo
              @keyup="searchKeyUp()"
            ></v-text-field>
            <v-btn
              icon
              size="secondary"
              @click="newSurfaceDecorationClick()"
              :disabled="!user.can('preset-surface-decoration.create')"
            >
              <v-icon small>fas fa-plus</v-icon>
            </v-btn>
          </v-toolbar>

          <v-data-table
            :class="{ 'mt-5': true, 'clickable': user.can('preset-surface-decoration.update') }"          
            :headers="surfaceDecorationHeaders"
            :items="surfaceDecorations"
            :loading="surfaceDecorationIsSearching"
            :options.sync="surfaceDecorationOptions"
            :server-items-length="surfaceDecorationTotalCount"
            :footer-props="{ 'items-per-page-options': [5, 10, 50] }"
            @click:row="rowSurfaceDecorationClick"
          >

            <template v-slot:item.excludes="{ value }">
              <v-chip-group multiple>
                <v-chip
                  v-for="id in value"
                  :key="id"
                >{{ formatSurface(id) }}</v-chip>
              </v-chip-group>
            </template>

            <template v-slot:item.requires="{ value }">
              <v-chip-group multiple>
                <v-chip
                  v-for="id in value"
                  :key="id"
                >{{ formatSurface(id) }}</v-chip>
              </v-chip-group>
            </template>

          </v-data-table>

          <preset-surface-decoration-dialog
            v-model="surfaceDecorationDialog"
            :model="surfaceDecoration"
            :errors="surfaceDecorationErrors"
            :decorations="decorations"
            :surfaces="surfaces"
            @save="saveSurfaceDecorationClick()"
            @delete="(item) => deleteSurfaceDecorationItem(item)"
          ></preset-surface-decoration-dialog>

        </v-card>
      </v-col>
    </v-row>


  </div>
</template>

<script>
import { inject, ref, watch } from 'vue'
import PresetDialog from '@/components/PresetDialog'
import PresetSurfaceDecorationDialog from '@/components/PresetSurfaceDecorationDialog'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useRouter, useRoute } from 'vue-router/composables'

export default {
  components: {
    PresetDialog,
    PresetSurfaceDecorationDialog,
    ConfirmDialog,
  },
  setup() {
    const api = inject('api')
    const { user } = api
    const router = useRouter()
    const route = useRoute()

    const decorationCollection = api.collection('decorations')
    const { items: decorations } = decorationCollection.all()

    const surfaceCollection = api.collection('surfaces')
    const { items: surfaces } = surfaceCollection.all()

    const presetModel = api.model('presets/:id', {
      params: {
        // expand: 'technique',
      },
    })

    const { attributes: preset, errors: presetErrors, oldAttributes: oldPreset } = presetModel
    const presetDialog = ref(false)
    const presetError = ref()

    function savePresetClick() {
      presetError.value = null
      presetModel.save().then(() => {
        presetDialog.value = false
      })
    }

    function deletePresetItem(item) {
      presetError.value = null
      if (user.can('preset.delete') && item?.id) {
        presetModel.delete(item.id).then(() => {
          presetModel.clear()
          presetDialog.value = false
          router.push({
            name: 'presets'
          })
        }).catch(() => {
          presetError.value = 'This preset is used in a product and cannot be deleted.'
        })
      }

    }

    const surfaceDecorationCollection = api.collection('preset-surface-decorations', {
      params: {
        expand: 'decoration,surface',
      },
      filter: {
        preset_id: 'eq',
        query: 'query:description',
      },
    })

    const { items: surfaceDecorations, filter: surfaceDecorationFilter, options: surfaceDecorationOptions, totalCount: surfaceDecorationTotalCount, isSearching: surfaceDecorationIsSearching } = surfaceDecorationCollection

    const surfaceDecorationHeaders = [
      { text: 'Decoration', value: 'decoration.description' },
      { text: 'Surface', value: 'surface.description' },
      { text: 'Excludes', value: 'excludes', sortable: false },
      { text: 'Requires', value: 'requires', sortable: false },
    ]

    const searchKeyUp = surfaceDecorationCollection.debounceSearch(300)

    const surfaceDecorationModel = api.model('preset-surface-decorations/:id')

    const { attributes: surfaceDecoration, errors: surfaceDecorationErrors } = surfaceDecorationModel
    const surfaceDecorationDialog = ref(false)

    function newSurfaceDecorationClick() {
      surfaceDecorationModel
        .clear()
        .setAttribute('preset_id', preset.value.id)
        .setAttribute('decoration_ids', [])
      surfaceDecorationDialog.value = true
    }

    function saveSurfaceDecorationClick() {
      const isNew = !surfaceDecoration.value.id

      if (!isNew) {
        surfaceDecorationModel.save().then(() => {
          surfaceDecorationCollection.search()
          surfaceDecorationDialog.value = false
        })
        return
      }

      const decorationIds = surfaceDecoration.value.decoration_ids || []

      if (decorationIds.length === 0) {
        surfaceDecorationErrors.value = { decoration_id: ['Decoration is required'] }
        return
      }

      surfaceDecorationErrors.value = {}

      const baseAttributes = {
        preset_id: preset.value.id,
        surface_id: surfaceDecoration.value.surface_id,
        excludes: surfaceDecoration.value.excludes,
        requires: surfaceDecoration.value.requires,
      }

      Promise.all(decorationIds.map(decoration_id =>
        api.client.post('preset-surface-decorations', { ...baseAttributes, decoration_id })
      )).then(() => {
        surfaceDecorationCollection.search()
        surfaceDecorationDialog.value = false
      }).catch(error => {
        if (error?.response?.status === 422) {
          const errors = {}
          ;(error.response.data || []).forEach(({ field, message }) => {
            if (!errors[field]) errors[field] = []
            errors[field].push(message)
          })
          surfaceDecorationErrors.value = errors
        }
      })
    }

    function rowSurfaceDecorationClick(item) {
      if (user.can('preset-surface-decoration.update')) {
        surfaceDecorationModel.clear().populate(item)
        surfaceDecorationDialog.value = true
      }
    }

    function editPresetClick() {
      presetError.value = null
      presetModel.reset()
      presetDialog.value = true
    }

    function duplicatePreset() {
      if (!preset.value?.id) {
        return
      }
      api.client.post(`presets/${preset.value.id}/duplicate`).then(response => {
        const newId = response?.data?.id
        if (newId) {
          router.push({ name: 'preset', params: { id: newId } })
        }
      })
    }

    function deleteSurfaceDecorationItem(surfaceDecorationItem) {
      if (user.can('preset-surface-decoration.delete') && surfaceDecorationItem?.id) {
        surfaceDecorationModel.delete(surfaceDecorationItem.id).then(() => {
          surfaceDecorationModel.clear()
          surfaceDecorationCollection.search()
          surfaceDecorationDialog.value = false
        })
      }
    }

    function formatSurface(id) {
      return surfaces.value?.find(surface => surface.id === id)?.description ?? null
    }


    watch(() => route.params.id, (id) => {
      if (id) {
        presetModel.find(id).catch(() => {
          router.push({
            name: 'presets',
          })
        })
        surfaceDecorationCollection.setFilter('preset_id', id).search()
      }
    }, { immediate: true })


    return {
      user: api.user,

      preset,
      presetErrors,
      oldPreset,
      presetDialog,
      editPresetClick,
      savePresetClick,
      duplicatePreset,

      surfaceDecorations,
      surfaceDecorationHeaders,
      surfaceDecorationFilter,
      surfaceDecorationOptions,
      surfaceDecorationTotalCount,
      surfaceDecorationIsSearching,

      surfaceDecoration,
      surfaceDecorationErrors,
      surfaceDecorationDialog,
      newSurfaceDecorationClick,
      saveSurfaceDecorationClick,
      rowSurfaceDecorationClick,
      deleteSurfaceDecorationItem,

      searchKeyUp,

      decorations,
      surfaces,
      deletePresetItem,
      presetError,
      formatSurface,
    }

  },
}
</script>

<style scoped>

:deep(.v-data-table.clickable table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  cursor: pointer;
}

dl.table dt, dl.table dd {
  padding-top: 2px;
  padding-bottom: 2px;
}


</style>